import { O_REALESTATE_TYPES_PROJECT } from "./../../../shared/model/const/property/real-estate-type.const";
import type { IPropertyDetailsResponse } from "@/src-refactor/modules/store/qrtk/services/realestate/responses/property-details.response";
import dayjs from "dayjs";
import { getConditionStatusLabel } from "@/src-refactor/modules/shared/components/refactor/helpers/property/prepare-condition-status-label";
import { prepareDetailPropertyPrice } from "./detail/prepareDetailPropertyPrice";

import { prepareDetailPropertyName } from "./detail/prepareDetailPropertyName";
import { prepareDetailPropertyImages } from "./detail/prepareDetailPropertyImages";
import { prepareDetailPropertyDescription } from "./detail/prepareDetailPropertyDescription";
import { prepareDetailPropertyStatus } from "./detail/prepareDetailPropertyStatus";
import { prepareDetailPropertyPosition } from "./detail/prepareDetailPropertyPosition";
import { prepareDetailPropertyUsage } from "./detail/prepareDetailPropertyUsage";
import { prepareDetailPropertyImprovements } from "./detail/prepareDetailPropertyImprovements";
import { prepareDetailPropertyAgents } from "./detail/prepareDetailPropertyAgents";
import { prepareDetailPropertyOwners } from "./detail/prepareDetailPropertyOwners";
import { prepareDetailPropertyCommission } from "./detail/prepareDetailPropertyCommission";
import { prepareDetailPropertyDocuments } from "./detail/prepareDetailPropertyDocuments";
import { prepareDetailPropertyValidations } from "./detail/prepareDetailPropertyValidations";
import { prepareDetailPropertyAlertsValidation } from "./detail/prepareDetailPropertyAlertsValidation";
import {
  prepareDetailPropertyFeatures,
  type IPropertyDetailFeatures
} from "./detail/prepareDetailPropertyFeatures";
import { prepareDetailPropertyBuildings } from "./detail/prepareDetailPropertyBuildings";
import { prepareDetailPropertyVisit } from "./detail/prepareDetailPropertyVisit";
import propertyBrochureAdapter from "./propertyBrochureAdapter";
import prepareImprovements from "@rf-helper/property/prepare-improments";
import {
  prepareDetailPropertyDataLog,
  type IPropertyDetailDataLog
} from "./detail/prepareDetailPropertyDataLog";
import {
  prepareDetailPropertyVirtualTour,
  type IPropertyDetailVirtualTour
} from "./detail/prepareDetailPropertyVirtualTour";
import {
  prepareDetailPropertyVideos,
  type IPropertyDetailVideos
} from "./detail/prepareDetailPropertyVideos";
import { prepareDetailPropertyVirtualFolders } from "./detail/prepareDetailPropertyVirtualFolders";
import {
  prepareDetailPropertyStatistics,
  type IPropertyDetailStatistics
} from "./detail/prepareDetailPropertyStatistics";
import { prepareDetailPropertyAlterestate } from "./detail/prepareDetailPropertyAlterestate";
import { preparePropertyDetailToCard } from "./detail/preparePropertyDetailToCard";
import { prepareDetailPropertyRemaxGlobal } from "./detail/prepareDetailPropertyRemaxGlobal";

const propertyDetailAdapter = (data: IPropertyDetailsResponse) => {
  const price = prepareDetailPropertyPrice(data);
  const brochure = propertyBrochureAdapter(data);

  const {
    characteristicsCabinets,
    characteristicsClosets,
    characteristicsDoors,
    characteristicsFloors,
    characteristicsFrames,
    characteristicsRoof,
    characteristicsRoofForm,
    characteristicsTypeHouse,
    characteristicsWindows
  } = prepareImprovements(data);

  const adaptedData = {
    id: data?.id,
    title: prepareDetailPropertyName(data),
    name: data?.name,
    images: prepareDetailPropertyImages(data),
    description: prepareDetailPropertyDescription(data),
    city: data?.city,
    sector: data?.sector,
    address: data?.address,
    latitude: data?.latitude,
    longitude: data?.longitude,
    status: prepareDetailPropertyStatus(data),
    condition:
      getConditionStatusLabel(data?.condition_status, data?.business_type) || null,
    position: prepareDetailPropertyPosition(data),
    usage: prepareDetailPropertyUsage(data),
    arrangementType: data?.arrangement || null,
    businessType: data?.business_type || null,
    improvementType: prepareDetailPropertyImprovements(data),
    propertyType: data?.realstate_type || null,
    projectType: data?.projecttype || null,
    projectTypeId: data?.projecttype
      ? O_REALESTATE_TYPES_PROJECT[data?.projecttype]?.id || null
      : null,
    isProject: Boolean(data?.project_id),
    agents: prepareDetailPropertyAgents(data),
    owners: prepareDetailPropertyOwners(data),
    commission: prepareDetailPropertyCommission(data),
    documents: prepareDetailPropertyDocuments(data),
    validations: prepareDetailPropertyValidations(data),
    alertsValidation: prepareDetailPropertyAlertsValidation(data),
    features: prepareDetailPropertyFeatures(data),
    buildings: prepareDetailPropertyBuildings(data),
    price,
    characteristics: [
      ...characteristicsCabinets,
      ...characteristicsClosets,
      ...characteristicsDoors,
      ...characteristicsFloors,
      ...characteristicsFrames,
      ...characteristicsRoof,
      ...characteristicsRoofForm,
      ...characteristicsTypeHouse,
      ...characteristicsWindows
    ],
    isCollection: Boolean(data?.is_collection),
    parentId: data?.parent_id ? Number(data?.parent_id) : null,
    refPoint: data?.reference || null,
    maintenanceFee: data?.maintenance_fee || null,
    visit: prepareDetailPropertyVisit(data),
    isInhabited: !Boolean(data.project)
      ? data?.visit_status === 1
        ? "Si"
        : "No"
      : null,
    trustName: data?.trust_name || null,
    deliveryDate: data?.delivers ? dayjs(data?.delivers).format("DD/MM/YYYY") : null,
    brochure: brochure,
    dataLog: prepareDetailPropertyDataLog(data),
    virtualTour: prepareDetailPropertyVirtualTour(data),
    videos: prepareDetailPropertyVideos(data),
    virtualFolders: prepareDetailPropertyVirtualFolders(data),
    statistics: prepareDetailPropertyStatistics(data),
    hasImportantAlert: Boolean(data?.has_important_alert),
    importantAlertMessage: data?.important_alert_message || "",
    alterestate: prepareDetailPropertyAlterestate(data),
    remaxGlobal: prepareDetailPropertyRemaxGlobal(data),
    hasRestorableUnits: Boolean(data?.has_restorable_units)
  };

  // Agregar cardProperty usando los datos adaptados
  return {
    ...adaptedData,
    cardProperty: preparePropertyDetailToCard(adaptedData, data)
  };
};

export default propertyDetailAdapter;

export type IPropertyDetailAdapter = ReturnType<typeof propertyDetailAdapter>;
